import React from 'react';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';
import { Companies, CompanyPage } from '../components/companies';
import { Contacts } from '../components/contacts';
import { ContactPage } from '../components/contacts/ContactPage';
import ContactGroups from '../components/groups/ContactGroups';
import ContactGroupPage from '../components/groups/ContactGroupPage';
import { Dashboard } from '../components/dashboard';
import { Products } from '../components/products';
import { ProductPage } from '../components/products/ProductPage';
import ProductGroups from '../components/products/ProductGroups';
import ProductGroupPage from '../components/products/ProductGroupPage';
import { PurchaseOrders } from '../components/purchase-orders';
import { PurchaseOrderPage } from '../components/purchase-orders/PurchaseOrderPage';
import { Sales } from '../components/sales';
import { SalePage } from '../components/sales/SalePage';
import SettingsRouter from '../components/settings/SettingsRouter';
import { Navigation } from '../components/navigation';
import { useStores } from '../stores';
import { observer } from 'mobx-react-lite';
import { Heading, minorScale, useToast } from '../npaper';
import Box from 'ui-box';
import { Logo } from '../components/Logo';

import { Spring, animated } from 'react-spring/renderprops';
import { Agreements } from '../components/agreements/Agreements';
import { AgreementPage } from '../components/agreements/AgreementPage';
import { QuickStartGuide } from '../components/dashboard/QuickStartGuide';

import { useMaintenanceCheck } from '../Maintenance';
import TasksTable from '../components/tasks/TasksTable';

export const AppRouter = observer(() => {
  useMaintenanceCheck();
  const { authStore } = useStores();

  const [ready, setReady] = React.useState(false);
  const [preparing, setPreparing] = React.useState(false);

  const history = useHistory();
  const toaster = useToast();

  const showQSG =
    authStore.currentOrganisation?.show_quick_start_guide || false;

  React.useEffect(() => {
    if (!authStore.isAuthorized) {
      return;
    }
    if (!authStore.me || !authStore.currentOrganisation || ready) {
      if (!preparing) {
        setPreparing(true);
        authStore
          .setup()
          .then(([me, orgs]) => {
            if (orgs.length === 0) {
              history.push('/auth/organisations');
            }
          })
          .catch((err) => {
            if (err.response && err.response.status === 401) {
              authStore.signOut();
            } else {
              toaster.showDefaultError(err);
            }
          });
      }
      return;
    }
    const timeout = setTimeout(() => {
      setReady(true);
    }, 300);
    return () => {
      clearTimeout(timeout);
    };
  }, [
    authStore,
    authStore.currentOrganisation,
    authStore.me,
    history,
    preparing,
    ready,
    toaster,
  ]);

  return !authStore.isAuthorized ? (
    <Redirect to="/auth" />
  ) : !ready ? (
    <Box
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Spring
        from={{
          opacity: 0,
          transform: 'translateY(-40px)',
        }}
        to={{
          opacity: 1,
          transform: 'translateY(0)',
        }}
      >
        {(props) => (
          <animated.div style={props}>
            <Logo marginBottom={minorScale(2)} width={64} height={64} />
            <Heading size={400} textAlign="center">
              sails
            </Heading>
          </animated.div>
        )}
      </Spring>
    </Box>
  ) : (
    <>
      <Navigation />
      <div className="App">
        <Switch>
          {showQSG ? (
            <Route
              exact
              path="/"
              render={() => <Redirect to="/quick-start-guide" />}
            />
          ) : (
            <Route exact path="/" component={Dashboard} />
          )}

          <Route exact path="/quick-start-guide" component={QuickStartGuide} />

          <Route exact path="/dashboard" component={Dashboard} />

          <Route exact path="/companies" component={Companies} />
          <Route path="/companies/:id" component={CompanyPage} />
          <Route exact path="/contacts" component={Contacts} />
          <Route path="/contacts/:id" component={ContactPage} />
          <Route exact path="/contact-groups" component={ContactGroups} />
          <Route path="/contact-groups/:id" component={ContactGroupPage} />

          <Route exact path="/sales" component={Sales} />
          <Route exact path="/offers" component={Sales} />
          <Route exact path="/orders" component={Sales} />
          <Route exact path="/invoices" component={Sales} />
          <Route path="/sales/:id" component={SalePage} />

          <Route exact path="/agreements" component={Agreements} />
          <Route path="/agreements/:id" component={AgreementPage} />

          <Route exact path="/products" component={Products} />
          <Route path="/products/:id" component={ProductPage} />
          <Route exact path="/product-groups" component={ProductGroups} />
          <Route path="/product-groups/:id" component={ProductGroupPage} />
          <Route exact path="/purchase-orders" component={PurchaseOrders} />
          <Route path="/purchase-orders/:id" component={PurchaseOrderPage} />

          <Route exact path="/tasks" component={TasksTable} />

          <SettingsRouter />

          <Route path="*" render={() => <Redirect to="/" />} />
        </Switch>
      </div>
    </>
  );
});
